import styled, { css, DefaultTheme } from 'styled-components';
import { MEDIA_QUERY } from '@optimus-web/breakpoints';
import {
    ANIMATION_CURVE,
    ANIMATION_DURATION,
    ELLIPSIS,
    getSpacingToken,
    getTextTokenPreset,
    getToken,
    Minifiable,
} from '@optimus-web/common';
import { Icon } from '../Icon';
import { Container } from '../Container';

interface ProgressIndicatorContainerProps {
    vertical?: boolean;
}

const COLOR_TRANSITION = css`
    transition-property: background-color, border-color, color, text-decoration-color;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-duration: ${ANIMATION_DURATION.default}ms;
`;

const STEP_TEXT_WIDTH = 140;
const STEP_TEXT_HEIGHT = getSpacingToken('700');
const STEP_ICON_POSITION_STYLES = css`
    margin-left: ${STEP_TEXT_WIDTH / 2}px;
    transform: translateX(-50%);
`;

const getLineStyles = ({
    isLastStep,
    editable,
    active,
    completed,
    isPrimary,
    vertical,
    nextIsVisited,
    theme,
}: StepInnerContainerProps & { theme: DefaultTheme }) => {
    if (isLastStep) {
        return;
    }

    const stepIconSize = getToken(active || completed || editable ? 'sizing300' : 'sizing100')({ theme });
    const horizontalLineGap = getToken(active || completed || editable ? 'sizing50' : 'spacing150')({ theme });
    const verticalLineTop = getSpacingToken(active || completed || editable ? '400' : '300')({ theme });
    const verticalLineGap = () => {
        const gaps = {
            short: getSpacingToken('200'),
            medium: getSpacingToken('300'),
            long: getSpacingToken('400'),
        };

        if ((active || completed || editable) && !nextIsVisited) {
            return gaps.medium({ theme });
        } else if (active || completed || editable || nextIsVisited) {
            return gaps.long({ theme });
        }

        return gaps.short({ theme });
    };

    const commonStyles = css`
        content: '';
        position: absolute;
        background-color: ${isPrimary ? getToken('borderInteractivePrimaryDefault') : getToken('borderStaticPrimary')};
    `;

    const variants = {
        horizontal: css`
            ${commonStyles}
            top: ${getSpacingToken('150')}px;
            left: ${`calc(${STEP_TEXT_WIDTH}px / 2 + ${stepIconSize / 2 + horizontalLineGap}px)`};
            height: ${getToken('borderWidth150')}px;
            width: calc(100% - ${getToken('sizing400')}px);
        `,
        vertical: css`
            ${commonStyles}
            width: ${getToken('borderWidth150')}px;
            height: calc(100% - ${verticalLineGap}px);
            left: calc((${getSpacingToken('300')}px - ${getToken('borderWidth150')}px) / 2);
            top: ${verticalLineTop}px;
        `,
    };

    return variants[vertical ? 'vertical' : 'horizontal'];
};

interface NavElementProps {
    isVerticalOpen?: boolean;
}

export const NavElement = styled.nav<NavElementProps>`
    ${({ isVerticalOpen }) =>
        isVerticalOpen &&
        css`
            flex-grow: 1;
        `}
`;

export const ProgressIndicatorContainer = styled.ol<ProgressIndicatorContainerProps>`
    width: 100%;
    overflow: hidden;
    margin: 0;
    padding: ${getSpacingToken('100')}px 0;
    list-style: none;
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    ${({ vertical }) =>
        vertical &&
        css`
            flex-direction: column;
            gap: ${getSpacingToken('100')}px;
        `}
`;

interface StepsWrapperProps {
    vertical?: boolean;
}

export const StepsWrapper = styled.div<StepsWrapperProps>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    ${({ vertical }) =>
        !vertical &&
        css`
            flex-direction: row;
            flex-wrap: nowrap;
            align-items: center;
        `}
`;

export const OpenableVerticalStepperContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0 ${getSpacingToken('200')}px;
`;

export const ToggleButtonElement = styled.button<Minifiable>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border: 0;
    background: transparent;
    padding: ${getSpacingToken('100')}px 0;
    cursor: pointer;
    order: 1;
    color: ${getToken('textStaticPrimary')};
`;

export const StepCountElement = styled.div`
    ${getTextTokenPreset('bodyMediumStrong')};
    color: ${getToken('textStaticTertiary')};
    margin-right: ${getSpacingToken('100')}px;
    min-width: ${getSpacingToken('1000')}px;
    text-align: right;
`;

interface StepWrapperProps {
    isLastStep: boolean;
    vertical?: boolean;
}

export const StepWrapper = styled.li<StepWrapperProps>`
    flex-grow: 1;
    flex-basis: 0;

    ${({ isLastStep, vertical }) => {
        if (isLastStep && !vertical) {
            return css`
                flex: none;
                width: ${STEP_TEXT_WIDTH}px;
            `;
        }
        if (isLastStep && vertical) {
            return css`
                ${StepInnerWrapper} {
                    min-height: auto;
                }
            `;
        }
    }}
`;

interface StepButtonProps extends Minifiable {
    clickable?: boolean;
    active?: boolean;
    completed?: boolean;
}

export const StepButton = styled.button<StepButtonProps>`
    flex-grow: 1;
    flex-basis: 0;
    width: 100%;
    box-sizing: border-box;
    background: transparent;
    border: 0;
    ${({ clickable }) =>
        clickable &&
        css`
            ${StepPrimaryTextElement} {
                text-decoration: ${getToken('textDecorationUnderline')};
                text-decoration-color: transparent;
                ${COLOR_TRANSITION};
            }
            &:hover {
                ${StepPrimaryTextElement} {
                    text-decoration-color: ${getToken('textStaticPrimary')};
                }

                ${StepNumberElement} {
                    background: ${getToken('backgroundInteractiveNeutralHover')};
                }
            }

            &:active {
                ${StepNumberElement} {
                    background: ${getToken('backgroundInteractiveNeutralActive')};
                }
            }
        `}

    ${({ completed }) =>
        completed &&
        css`
            &:hover {
                ${StepIconElement} {
                    background: ${getToken('backgroundInteractiveSecondaryHover')};
                    color: ${getToken('textInteractivePrimaryHover')};
                }
            }

            &:active {
                ${StepIconElement} {
                    background: ${getToken('backgroundInteractiveSecondaryActive')};
                    color: ${getToken('textInteractivePrimaryActive')};
                }
            }
        `}

    ${({ active, clickable, completed }) =>
        (clickable || completed) &&
        !active &&
        css`
            cursor: pointer;
            padding: 0;
        `}
`;

interface StepInnerContainerProps {
    editable?: boolean;
    completed?: boolean;
    active?: boolean;
    isLastStep: boolean;
    isPrimary: boolean;
    vertical?: boolean;
    nextIsVisited?: boolean;
    isOpenable?: boolean;
    isOpened?: boolean;
}

export const StepInnerWrapper = styled(Container)<StepInnerContainerProps>`
    display: flex;
    flex-direction: column;
    position: relative;
    text-align: center;
    width: 100%;
    gap: ${getSpacingToken('100')}px;

    &::after {
        ${getLineStyles}
    }

    ${({ vertical, isOpenable, isOpened }) =>
        vertical &&
        css`
            flex-direction: row;
            text-align: left;
            min-height: ${STEP_TEXT_HEIGHT}px;

            ${isOpenable &&
            !isOpened &&
            css`
                min-height: auto;
            `}
        `}
`;

interface StepTextWrapperProps {
    vertical?: boolean;
    isVerticalOpenActiveStep?: boolean;
}

export const StepTextWrapper = styled.div<StepTextWrapperProps>`
    ${({ vertical }) =>
        vertical
            ? css`
                  padding-top: ${getSpacingToken('25')}px;
              `
            : css`
                  max-width: ${STEP_TEXT_WIDTH}px;
                  min-width: ${getSpacingToken('700')}px;
              `}

    ${MEDIA_QUERY.tabletUp} {
        display: block;
    }
    word-wrap: break-word;

    ${({ isVerticalOpenActiveStep }) =>
        isVerticalOpenActiveStep &&
        css`
            width: 100%;
        `}
`;

interface StepPrimaryTextElementProps {
    clickable?: boolean;
    completed?: boolean;
    active?: boolean;
    isVerticalOpenActiveStep?: boolean;
}

export const StepPrimaryTextElement = styled.div<StepPrimaryTextElementProps>`
    ${getTextTokenPreset('bodyMediumStrong')};

    color: ${getToken('textStaticPrimary')};

    ${({ active, completed, clickable }) =>
        !active &&
        !completed &&
        !clickable &&
        css`
            color: ${getToken('textStaticTertiary')};
        `}

    ${({ isVerticalOpenActiveStep }) =>
        isVerticalOpenActiveStep &&
        css`
            display: flex;
            justify-content: space-between;
        `}
`;

export const StepSecondaryTextElement = styled.div`
    ${getTextTokenPreset('bodySmall')};
    color: ${getToken('textStaticTertiary')};
    ${ELLIPSIS}
`;

interface StepNumberIconProps {
    completed?: boolean;
    active?: boolean;
    primary?: boolean;
    singleItem?: boolean;
    clickable?: boolean;
    vertical?: boolean;
}

export const StepIconElement = styled(Icon)<StepNumberIconProps>`
    color: ${(props) => (props.primary ? getToken('textInteractivePrimaryDefault') : getToken('textStaticPrimary'))};
    border-radius: ${getToken('borderRadiusRound')};
    ${(props) =>
        props.active &&
        !props.singleItem &&
        css`
            background: ${getToken('backgroundInteractiveSecondaryDefault')};
            box-shadow: 0 0 0 ${getSpacingToken('100')}px ${getToken('backgroundInteractiveSecondaryDefault')};
        `}
    ${(props) =>
        (props.completed || (props.completed && props.clickable)) &&
        css`
            background: ${getToken('backgroundInteractiveSecondaryDefault')};
            width: ${getSpacingToken('300')}px;
            height: ${getSpacingToken('300')}px;
            display: flex;
            align-items: center;
            justify-content: center;
        `}
    ${COLOR_TRANSITION};
    ${({ vertical }) => !vertical && STEP_ICON_POSITION_STYLES};
`;

export const StepNumberElement = styled.span<
    Pick<StepNumberIconProps, 'active' | 'completed' | 'clickable' | 'vertical'>
>`
    box-sizing: border-box;
    ${getTextTokenPreset('bodyMediumStrong')};
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    margin: ${({ active, completed, clickable }) => (completed || active || clickable ? 0 : getSpacingToken('100'))}px;
    width: ${({ active, completed, clickable }) =>
        completed || active || clickable ? getSpacingToken('300') : getSpacingToken('100')}px;
    height: ${({ active, completed, clickable }) =>
        completed || active || clickable ? getSpacingToken('300') : getSpacingToken('100')}px;
    border: ${(props) => (!props.active && !props.clickable ? `solid` : '0')};
    border-width: ${(props) => (!props.active && !props.clickable ? getToken('borderWidth150') : 0)}px;
    border-color: ${getToken('borderStaticPrimary')};
    border-radius: ${getToken('borderRadiusRound')};
    ${(props) =>
        props.active &&
        css`
            background-color: ${getToken('backgroundInteractivePrimaryDefault')};
        `};

    ${(props) =>
        props.clickable &&
        css`
            background-color: ${getToken('backgroundInteractiveNeutralDefault')};
        `};

    ${(props) =>
        props.active &&
        css`
            color: ${getToken('textStaticInverse')};
        `};
    ${COLOR_TRANSITION};
    ${({ vertical }) => !vertical && STEP_ICON_POSITION_STYLES};
`;
